import { FunctionComponent, useEffect, useState } from 'react'
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Form,
  Alert,
  Spinner,
} from 'react-bootstrap'
import classnames from 'classnames'

import HeaderOutside from 'components/layout/headerOutside'
import Footer from '../../../components/layout/footer'
import resetPasswordCss from './passwordReset.module.scss'
import * as api from '../api.client'
import { AppMessage } from '../../../utilities/messages'
import { useParams } from 'react-router-dom'
import { PasswordResetInfo } from '../types'

const PasswordReset: FunctionComponent = () => {
  const { token } = useParams<{ token: string }>()
  const [showFormErrors, setShowFormErrors] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })
  const [resetingPassword, setResetingPassword] = useState(false)
  const [updatedCredentials, setUpdatedCredentials] =
    useState<PasswordResetInfo>({
      token: token as string,
      password: '',
      passwordConfirmation: '',
    })

  useEffect(() => {
    if (!token) {
      setAppMessage({
        show: true,
        success: false,
        message:
          'O token necessário para redefinir a senha não foi encontrado.',
      })
    }
  }, [token])

  const resetPassword = async (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formIsValid = event.currentTarget.checkValidity()

    if (!formIsValid) {
      setShowFormErrors(true)
      return
    }

    setResetingPassword(true)

    try {
      await api.resetPassword(updatedCredentials)
      setAppMessage({
        show: true,
        success: true,
        message: 'Senha redefinida com sucesso.',
      })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: (error as any).messages.join(', '),
      })
    } finally {
      setResetingPassword(false)
    }
  }

  const handleFormChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = target

    setUpdatedCredentials({ ...updatedCredentials, [name]: value })
  }

  return (
    <div>
      <HeaderOutside></HeaderOutside>

      <Container fluid className={resetPasswordCss.container}>
        <Row
          className={classnames(
            'justify-content-md-center',
            resetPasswordCss.row
          )}
        >
          <Col md="4" className={resetPasswordCss.col}>
            <Card className="mb-2">
              <Card.Body>
                <h5 className={resetPasswordCss.title}>Redefinir senha</h5>

                {appMessage.show && (
                  <Alert
                    variant={appMessage.success ? 'success' : 'danger'}
                    style={{ marginTop: 20 }}
                  >
                    {appMessage.message}
                  </Alert>
                )}

                <Form
                  noValidate
                  validated={showFormErrors}
                  onSubmit={resetPassword}
                >
                  <Form.Group className={resetPasswordCss.formGroup}>
                    <Form.Label>Nova senha</Form.Label>
                    <Form.Control
                      type="password"
                      className={resetPasswordCss.formControl}
                      name="password"
                      value={updatedCredentials.password}
                      onChange={handleFormChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Informe uma senha.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Confirme sua nova senha</Form.Label>
                    <Form.Control
                      type="password"
                      className={resetPasswordCss.formControl}
                      name="passwordConfirmation"
                      value={updatedCredentials.passwordConfirmation}
                      onChange={handleFormChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Confirme sua nova senha.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className={resetPasswordCss.button}
                    disabled={!token}
                  >
                    {resetingPassword && (
                      <div>
                        <Spinner size="sm" animation="border" variant="dark" />{' '}
                        Processando...
                      </div>
                    )}

                    {!resetingPassword && <div>Redefinir senha</div>}
                  </Button>

                  <Row>
                    <Col sm="12" className={resetPasswordCss.backToLogin}>
                      <a href="/login" className={resetPasswordCss.backToLogin}>
                        Voltar para o login
                      </a>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </Container>
    </div>
  )
}

export default PasswordReset
